import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, getDocs, doc, setDoc, updateDoc, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { PlusIcon, TicketIcon } from '@heroicons/react/24/outline';

const AdminCouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [formCode, setFormCode] = useState('');
  const [formCredits, setFormCredits] = useState('');
  const [formMaxRedemptions, setFormMaxRedemptions] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [formDescription, setFormDescription] = useState('');

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const q = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCoupons(list);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const code = formCode.trim().toUpperCase();
    if (!code) { toast.error('Code is required'); return; }
    const credits = parseInt(formCredits, 10);
    if (!credits || credits <= 0) { toast.error('Credits must be a positive number'); return; }

    setCreating(true);
    try {
      const db = getFirestore();
      const auth = getAuth();
      const uid = auth.currentUser?.uid || '';

      const couponData = {
        code,
        credits,
        maxRedemptions: formMaxRedemptions ? parseInt(formMaxRedemptions, 10) : null,
        currentRedemptions: 0,
        expiresAt: formExpiresAt ? Timestamp.fromDate(new Date(formExpiresAt + 'T23:59:59')) : null,
        isActive: true,
        description: formDescription.trim(),
        createdAt: serverTimestamp(),
        createdBy: uid,
      };

      const couponRef = doc(collection(db, 'coupons'));
      await setDoc(couponRef, couponData);

      toast.success(`Coupon "${code}" created`);
      setFormCode('');
      setFormCredits('');
      setFormMaxRedemptions('');
      setFormExpiresAt('');
      setFormDescription('');
      setShowCreateForm(false);
      fetchCoupons();
    } catch (err) {
      console.error('Error creating coupon:', err);
      toast.error('Failed to create coupon: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'coupons', coupon.id), {
        isActive: !coupon.isActive,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Coupon "${coupon.code}" ${coupon.isActive ? 'deactivated' : 'activated'}`);
      fetchCoupons();
    } catch (err) {
      console.error('Error toggling coupon:', err);
      toast.error('Failed to update coupon');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  const isExpired = (coupon) => {
    if (!coupon.expiresAt) return false;
    const d = coupon.expiresAt.toDate ? coupon.expiresAt.toDate() : new Date(coupon.expiresAt);
    return d < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Coupons</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {showCreateForm ? 'Cancel' : 'Create Coupon'}
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">New Coupon</h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code</label>
                <input
                  type="text"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME50"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Credits to Award</label>
                <input
                  type="number"
                  min="1"
                  value={formCredits}
                  onChange={(e) => setFormCredits(e.target.value)}
                  placeholder="e.g. 100"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Redemptions <span className="text-gray-400">(optional)</span></label>
                <input
                  type="number"
                  min="1"
                  value={formMaxRedemptions}
                  onChange={(e) => setFormMaxRedemptions(e.target.value)}
                  placeholder="Leave blank for unlimited"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Expiration Date <span className="text-gray-400">(optional)</span></label>
                <input
                  type="date"
                  value={formExpiresAt}
                  onChange={(e) => setFormExpiresAt(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description <span className="text-gray-400">(internal note)</span></label>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="e.g. Beta tester reward"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Coupon'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Coupon list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No coupons yet. Create one to get started.
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redemptions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coupons.map((coupon) => {
                const expired = isExpired(coupon);
                const maxedOut = coupon.maxRedemptions != null && (coupon.currentRedemptions || 0) >= coupon.maxRedemptions;
                return (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TicketIcon className="h-5 w-5 text-indigo-500 mr-2" />
                        <span className="text-sm font-mono font-medium text-gray-900">{coupon.code}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{coupon.credits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.currentRedemptions || 0}
                      {coupon.maxRedemptions != null && ` / ${coupon.maxRedemptions}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coupon.expiresAt ? (
                        <span className={expired ? 'text-red-600' : ''}>{formatDate(coupon.expiresAt)}</span>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {!coupon.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactive</span>
                      ) : expired ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Expired</span>
                      ) : maxedOut ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Maxed Out</span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{coupon.description || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => toggleActive(coupon)}
                        className={`text-sm font-medium ${coupon.isActive ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                      >
                        {coupon.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCouponsPage;
